import React from "react";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon,
  CreditCardIcon,
  CashIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/solid";

const getStepIcon = (stage) => {
  const isRejected =
    stage.code === "REJECTED" || (stage.status === "COMPLETED" && stage.code === "REJECTED");
  if (isRejected) {
    return <XCircleIcon className="w-5 h-5 text-red-500" />;
  }

  if (stage.status === "COMPLETED") {
    return <CheckCircleIcon className="w-5 h-5 text-emerald-500" />;
  }

  if (stage.status === "CURRENT") {
    return <ClockIcon className="w-5 h-5 text-blue-600 animate-pulse" />;
  }

  // UPCOMING
  return (
    <div className="w-4 h-4 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center">
      <div className="w-1.5 h-1.5 rounded-full bg-gray-300" />
    </div>
  );
};

const getStageStyles = (status, code) => {
  if (code === "REJECTED") {
    return {
      card: "bg-red-50/50 border-red-200",
      badge: "bg-red-100 text-red-700",
    };
  }
  if (status === "COMPLETED") {
    return {
      card: "bg-emerald-50/40 border-emerald-200/80",
      badge: "bg-emerald-100 text-emerald-800",
    };
  }
  if (status === "CURRENT") {
    return {
      card: "bg-blue-50/50 border-blue-300 shadow-xs ring-1 ring-blue-200",
      badge: "bg-blue-100 text-blue-800 font-bold",
    };
  }
  return {
    card: "bg-gray-50/60 border-gray-200 opacity-75",
    badge: "bg-gray-100 text-gray-500",
  };
};

const statusLabels = {
  COMPLETED: "Completed",
  CURRENT: "In Progress",
  UPCOMING: "Upcoming",
};

const FundRequestLifecycleTimeline = ({ lifecycle = [] }) => {
  if (!lifecycle || lifecycle.length === 0) {
    return (
      <div className="py-4 text-center text-gray-400 text-sm">
        No lifecycle tracking history available.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {lifecycle.map((stepItem, index) => {
        const isCompleted = stepItem.status === "COMPLETED";
        const isCurrent = stepItem.status === "CURRENT";
        const styles = getStageStyles(stepItem.status, stepItem.code);

        return (
          <div
            key={stepItem.step || index}
            className={`rounded-xl border p-3.5 flex flex-col justify-between transition-all ${styles.card}`}
          >
            {/* Header: Step & Badge */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  {getStepIcon(stepItem)}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Step {stepItem.step || index + 1}
                  </span>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-md font-semibold ${styles.badge}`}>
                  {statusLabels[stepItem.status] || stepItem.status}
                </span>
              </div>

              {/* Title */}
              <h4 className="text-sm font-bold text-gray-900 leading-snug">
                {stepItem.title}
              </h4>

              {/* Description */}
              {stepItem.description && (
                <p className="text-xs text-gray-600 leading-relaxed">
                  {stepItem.description}
                </p>
              )}
            </div>

            {/* Contextual Extra Info (Amounts / Reference / Remarks / Date) */}
            <div className="mt-2.5 pt-2 border-t border-gray-200/60 space-y-1.5 text-xs">
              {/* Amounts breakdown */}
              {(stepItem.approved_amount !== undefined ||
                stepItem.rejected_amount !== undefined ||
                stepItem.paid_amount !== undefined) && (
                <div className="flex flex-wrap gap-1.5">
                  {stepItem.approved_amount !== undefined && stepItem.approved_amount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-green-100 text-green-800 font-semibold text-[11px]">
                      <CashIcon className="w-3.5 h-3.5 mr-1" />
                      ₹{stepItem.approved_amount}
                    </span>
                  )}
                  {stepItem.rejected_amount !== undefined && stepItem.rejected_amount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-red-100 text-red-800 font-semibold text-[11px]">
                      <ExclamationCircleIcon className="w-3.5 h-3.5 mr-1" />
                      ₹{stepItem.rejected_amount}
                    </span>
                  )}
                  {stepItem.paid_amount !== undefined && stepItem.paid_amount > 0 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 font-semibold text-[11px]">
                      <CreditCardIcon className="w-3.5 h-3.5 mr-1" />
                      ₹{stepItem.paid_amount}
                    </span>
                  )}
                </div>
              )}

              {/* Reference */}
              {stepItem.payment_reference && (
                <div className="text-[11px] font-semibold text-purple-700 bg-purple-100/70 px-2 py-0.5 rounded-md flex items-center">
                  <DocumentTextIcon className="w-3.5 h-3.5 mr-1" />
                  Ref: {stepItem.payment_reference}
                </div>
              )}

              {/* Remarks */}
              {stepItem.remarks && (
                <div className="text-[11px] text-gray-700 bg-white/80 p-1.5 rounded-md border border-gray-200">
                  <span className="font-bold text-gray-500 uppercase text-[9px] block">Note</span>
                  {stepItem.remarks}
                </div>
              )}

              {/* Date time */}
              {stepItem.date_time && (
                <div className="flex items-center text-[11px] text-gray-500 font-medium">
                  <ClockIcon className="w-3.5 h-3.5 mr-1 text-gray-400" />
                  <span>{stepItem.date_time}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FundRequestLifecycleTimeline;
